// Pure shop logic for the UI (no React): money, the cart, discount hints. Unit-tested in shop.test.js.

export const money = (cents) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

export const MAX_LINE_QUANTITY = 48;
export const MAX_ORDER_QUANTITY = 96;

// The same tiers as the API's GET /api/pricing (the API is the authority; these only drive hints before checkout).
export const DEFAULT_TIERS = [
  { minQuantity: 24, percent: 15, label: 'Box of 24+' },
  { minQuantity: 12, percent: 10, label: 'Box of 12+' },
];

// cart: { [productId]: quantity }
export function cartReducer(cart, action) {
  switch (action.type) {
    case 'add': {
      const next = Math.min((cart[action.id] || 0) + (action.quantity || 1), MAX_LINE_QUANTITY, action.stock ?? Infinity);
      return next > 0 ? { ...cart, [action.id]: next } : cart;
    }
    case 'set': {
      const { [action.id]: _, ...rest } = cart;
      const q = Math.min(Math.max(0, Math.floor(action.quantity || 0)), MAX_LINE_QUANTITY, action.stock ?? Infinity);
      return q > 0 ? { ...rest, [action.id]: q } : rest;
    }
    case 'remove': {
      const { [action.id]: _, ...rest } = cart;
      return rest;
    }
    case 'clear':
      return {};
    default:
      return cart;
  }
}

// The cart's lines joined with the catalogue (lines for products no longer listed are dropped), and its prices.
export function summarize(cart, products, tiers = DEFAULT_TIERS) {
  const byId = new Map(products.map((p) => [p.id, p]));
  const lines = Object.entries(cart)
    .map(([id, quantity]) => ({ product: byId.get(Number(id)), quantity }))
    .filter((l) => l.product)
    .map((l) => ({ ...l, lineTotalCents: l.quantity * l.product.priceCents }));
  const quantity = lines.reduce((n, l) => n + l.quantity, 0);
  const subtotalCents = lines.reduce((n, l) => n + l.lineTotalCents, 0);
  const sorted = [...tiers].sort((a, b) => b.minQuantity - a.minQuantity);
  const tier = sorted.find((t) => quantity >= t.minQuantity) || null;
  const discountCents = tier ? Math.ceil((subtotalCents * tier.percent) / 100) : 0;
  const nextTier = [...sorted].reverse().find((t) => quantity < t.minQuantity) || null;
  return {
    lines,
    quantity,
    subtotalCents,
    discountCents,
    totalCents: subtotalCents - discountCents,
    tier,
    hint: nextTier ? `Add ${nextTier.minQuantity - quantity} more for ${nextTier.percent}% off (${nextTier.label})` : null,
    tooMany: quantity > MAX_ORDER_QUANTITY,
  };
}

// Pickup dates the API accepts: tomorrow .. 60 days ahead, as YYYY-MM-DD (UTC, like the API).
export function pickupRange(now = new Date()) {
  const day = (offset) => new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + offset)).toISOString().slice(0, 10);
  return { min: day(1), max: day(60) };
}

export const STATUS_TEXT = {
  received: 'Received: the kitchen will start soon',
  baking: 'Baking',
  ready: 'Ready for pickup',
  collected: 'Collected',
  cancelled: 'Cancelled',
};

// The page from the URL hash: #/order/<uuid> shows an order, #/macaron/<slug> a product, #/help the user guide,
// anything else the shop.
export function parseRoute(hash) {
  const order = /^#\/order\/([0-9a-f-]{36})$/.exec(hash || '');
  if (order) return { page: 'order', id: order[1] };
  if (/^#\/help\/?$/.test(hash || '')) return { page: 'help' };
  const product = /^#\/macaron\/([a-z0-9-]{1,64})$/.exec(hash || '');
  return product ? { page: 'product', slug: product[1] } : { page: 'shop' };
}

// How far the box is towards its next discount tier (0..1), for the progress bar in the box.
export function tierProgress(quantity, tiers = DEFAULT_TIERS) {
  const next = [...tiers].sort((a, b) => a.minQuantity - b.minQuantity).find((t) => quantity < t.minQuantity);
  if (!next) return { next: null, ratio: 1 };
  return { next, ratio: Math.max(0, Math.min(1, quantity / next.minQuantity)) };
}

// window.localStorage itself can throw (storage blocked); then there is simply no storage.
export function browserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

// A checkout's idempotency key (UUID v4). crypto.randomUUID needs a secure context; getRandomValues does not.
export function newCheckoutKey(c = typeof window !== 'undefined' ? window.crypto : undefined) {
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

// Saved cart (per browser): storage can be missing or throw (private mode); then the cart is simply not kept.
export function loadCart(storage) {
  try {
    const raw = storage && storage.getItem('macaron-cart');
    const parsed = raw ? JSON.parse(raw) : {};
    return Object.fromEntries(
      Object.entries(parsed).filter(([id, q]) => /^\d+$/.test(id) && Number.isInteger(q) && q > 0 && q <= MAX_LINE_QUANTITY),
    );
  } catch {
    return {};
  }
}

export function saveCart(storage, cart) {
  try {
    if (storage) storage.setItem('macaron-cart', JSON.stringify(cart));
  } catch {
    /* not kept */
  }
}
